import React, { useEffect, useState } from 'react';
import {
  getUsers, createUser, updateUser, deleteUser,
  getUserAssignments, updateUserAssignments,
  getShops, getFactories,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'super_admin', label: 'Διαχειριστής', badge: 'badge-blue' },
  { value: 'shop_worker', label: 'Κατάστημα', badge: 'badge-green' },
  { value: 'factory_worker', label: 'Εργοστάσιο', badge: 'badge-amber' },
  { value: 'driver', label: 'Οδηγός', badge: 'badge-gray' },
];

const labelFor = (role) => ROLES.find((r) => r.value === role)?.label || role;
const badgeFor = (role) => ROLES.find((r) => r.value === role)?.badge || 'badge-gray';

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', full_name: '', role: 'shop_worker', password: '' });
  const [assignedShops, setAssignedShops] = useState([]);
  const [assignedFactories, setAssignedFactories] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([
      getUsers(showInactive),
      getShops(false),
      getFactories(false),
    ])
      .then(([uRes, sRes, fRes]) => {
        setUsers(uRes.data);
        setShops(sRes.data);
        setFactories(fRes.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Σφάλμα'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm({ username: '', full_name: '', role: 'shop_worker', password: '' });
    setAssignedShops([]);
    setAssignedFactories([]);
    setShowModal(true);
  };

  const openEdit = async (u) => {
    setEditing(u);
    setForm({ username: u.username, full_name: u.full_name, role: u.role, password: '' });
    setAssignedShops([]);
    setAssignedFactories([]);
    setShowModal(true);
    try {
      const r = await getUserAssignments(u.id);
      setAssignedShops(r.data.shops || []);
      setAssignedFactories(r.data.factories || []);
    } catch { /* ignore - just won't pre-check */ }
  };

  const toggleAssignedShop = (id) => {
    setAssignedShops((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };
  const toggleAssignedFactory = (id) => {
    setAssignedFactories((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const save = async () => {
    if (!editing) {
      if (!form.username.trim() || !form.full_name.trim() || !form.password) {
        return toast.error('Συμπληρώστε όλα τα πεδία');
      }
      if (form.password.length < 4) return toast.error('Ο κωδικός πρέπει να έχει τουλάχιστον 4 χαρακτήρες');
    } else {
      if (!form.full_name.trim()) return toast.error('Συμπληρώστε όνομα');
      if (form.password && form.password.length < 4) return toast.error('Ο κωδικός πρέπει να έχει τουλάχιστον 4 χαρακτήρες');
    }
    if (form.role === 'driver' && assignedShops.length === 0 && assignedFactories.length === 0) {
      return toast.error('Επιλέξτε τουλάχιστον ένα εργοστάσιο ή κατάστημα για τον οδηγό');
    }
    setSaving(true);
    try {
      let userId;
      if (editing) {
        const payload = { full_name: form.full_name.trim(), role: form.role };
        if (form.password) payload.password = form.password;
        await updateUser(editing.id, payload);
        userId = editing.id;
      } else {
        const r = await createUser({
          username: form.username.trim(),
          full_name: form.full_name.trim(),
          role: form.role,
          password: form.password,
        });
        userId = r.data.id;
      }
      // Save assignments based on role
      const assignPayload = {};
      if (form.role === 'shop_worker') { assignPayload.shops = assignedShops; assignPayload.factories = []; }
      else if (form.role === 'factory_worker') { assignPayload.shops = []; assignPayload.factories = assignedFactories; }
      // A driver is assigned both sides and sees an order touching either
      else if (form.role === 'driver') { assignPayload.shops = assignedShops; assignPayload.factories = assignedFactories; }
      else { assignPayload.shops = []; assignPayload.factories = []; }
      await updateUserAssignments(userId, assignPayload);
      toast.success(editing ? 'Ενημερώθηκε' : 'Δημιουργήθηκε');
      setShowModal(false);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (u) => {
    if (u.id === me.id) return toast.error('Δεν μπορείτε να διαγράψετε τον εαυτό σας');
    if (!window.confirm(`Απενεργοποίηση χρήστη: ${u.full_name};`)) return;
    try { await deleteUser(u.id, false); toast.success('Απενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const reactivate = async (u) => {
    try { await updateUser(u.id, { is_active: true }); toast.success('Ενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const hardDelete = async (u) => {
    if (u.id === me.id) return toast.error('Δεν μπορείτε να διαγράψετε τον εαυτό σας');
    if (!window.confirm(`ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ χρήστη: ${u.full_name};\nΑυτή η ενέργεια δεν αναιρείται.`)) return;
    try { await deleteUser(u.id, true); toast.success('Διαγράφηκε οριστικά'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div><h2>👥 Χρήστες</h2><p>Διαχείριση λογαριασμών χρηστών</p></div>
          <div className="flex-row">
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            <button className="btn btn-primary" onClick={openCreate}>+ Νέος Χρήστης</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="page-loading"><span className="loading-spinner dark" /></div>
            ) : users.length === 0 ? (
              <div className="empty-state"><div>👥</div><p>Δεν υπάρχουν χρήστες</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {users.map((u) => (
                  <div key={u.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    border: '1px solid var(--gray-100)', borderRadius: 8,
                    background: u.is_active ? 'white' : 'var(--gray-50)',
                    opacity: u.is_active ? 1 : 0.6,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: 'var(--gray-800)' }}>
                        {u.full_name}
                        {!u.is_active && <span style={{ color: 'var(--gray-400)', fontWeight: 400, marginLeft: 6 }}>(ανενεργό)</span>}
                        {u.id === me.id && <span style={{ color: 'var(--blue)', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>(εσείς)</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>@{u.username}</div>
                    </div>
                    <span className={'badge ' + badgeFor(u.role)}>{labelFor(u.role)}</span>
                    <div className="flex-row">
                      {u.is_active ? (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>✏️</button>
                          <button className="btn btn-danger btn-sm" onClick={() => softDelete(u)} disabled={u.id === me.id}>🗑</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={() => reactivate(u)} title="Επαναφορά">↻</button>
                          <button className="btn btn-danger btn-sm" onClick={() => hardDelete(u)} disabled={u.id === me.id} title="Οριστική Διαγραφή">❌</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>{editing ? 'Επεξεργασία Χρήστη' : 'Νέος Χρήστης'}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Username *</label>
                <input className="form-input" value={form.username} disabled={!!editing}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder={editing ? '' : 'π.χ. giorgos'} autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Πλήρες όνομα *</label>
                <input className="form-input" value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Ρόλος *</label>
                <select className="form-select" value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{editing ? 'Νέος κωδικός (προαιρετικός)' : 'Κωδικός *'}</label>
                <input className="form-input" type="password" value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder={editing ? 'Αφήστε κενό για διατήρηση' : 'Τουλάχιστον 4 χαρακτήρες'} />
              </div>

              {form.role === 'driver' && (
                <div style={{
                  background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
                  borderRadius: 8, padding: '10px 12px', fontSize: 12.5,
                  color: 'var(--gray-600)', marginBottom: 14, lineHeight: 1.55,
                }}>
                  🚚 Ο οδηγός βλέπει <strong>μόνο</strong> τις παραγγελίες — σε κάθε κατάσταση —
                  που είτε φτιάχνονται σε ένα από τα εργοστάσιά του είτε παραλαμβάνονται
                  από ένα από τα καταστήματά του. Δεν μπορεί να αλλάξει τίποτα.
                </div>
              )}

              {(form.role === 'factory_worker' || form.role === 'driver') && (
                <div className="form-group">
                  <label className="form-label">
                    {form.role === 'driver' ? 'Εργοστάσια του οδηγού' : 'Εργοστάσια'}
                  </label>
                  {factories.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν εργοστάσια.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                      {factories.map((f) => (
                        <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                          <input type="checkbox" checked={assignedFactories.includes(f.id)} onChange={() => toggleAssignedFactory(f.id)} />
                          {f.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(form.role === 'shop_worker' || form.role === 'driver') && (
                <div className="form-group">
                  <label className="form-label">
                    {form.role === 'driver' ? 'Καταστήματα του οδηγού' : 'Καταστήματα'}
                  </label>
                  {shops.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν καταστήματα.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                      {shops.map((s) => (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                          <input type="checkbox" checked={assignedShops.includes(s.id)} onChange={() => toggleAssignedShop(s.id)} />
                          {s.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
